<template>
    <div class="explanation-part">
        <div
            class="assignment-explanation-header"
            @click="collapse()"
            :class="collapsed && 'active'">
            <div class="assignment-explanation-header-title">
                <div class="drag-handle">
                    <i
                        class="fas fa-fw fa-arrows-alt-v sub-assignment-part-explanation-drag-handle"
                        v-if="canEditPortfolio"></i>
                </div>
                <div class="title">{{ $t('assignment.part.explanation') + ' ' + partIn.order }}</div>
            </div>

            <b-badge
                class="badge-part-type"
                variant="info"
                >{{ partIn.getAssignmentPartType() }}</b-badge
            >

            <i class="fas fa-fw fa-chevron-down state-icon"></i>

            <b-dropdown
                class="ellipsis-nav explanation-nav"
                v-if="canEditPortfolio"
                no-caret
                variant="primary"
                right
                toggle-class="explanation-context-menu">
                <template v-slot:button-content>
                    <i class="fas fa-ellipsis-v"></i>
                </template>

                <b-dropdown-item
                    class="btn-edit edit-sub-assignment-part"
                    @click.prevent.stop="editPart">
                    {{ $t('assignment.edit_part') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="copy-sub-assignment-part"
                    @click.prevent.stop="copyPart">
                    <i class="btn-duplicate"></i>{{ $t('assignment.copy_part') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="remove-sub-assignment-part"
                    @click.prevent.stop="onRemovePart()">
                    <i class="btn-remove"></i>{{ $t('assignment.delete_part') }}
                </b-dropdown-item>
            </b-dropdown>
            <design-2021-assignment-edit-update-explanation-part-modal-component :part-in="partIn">
            </design-2021-assignment-edit-update-explanation-part-modal-component>
        </div>
        <b-collapse
            class="part-content"
            v-model="collapsed"
            :id="'collapse-part-' + partIn.id"
            role="tabpanel">
            <div class="explanation-content">
                <design-2021-apprentice-display-html-component
                    class="explanation-text explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationText()"
                    :html-content="partIn.text.value"></design-2021-apprentice-display-html-component>

                <div
                    class="explanation-images explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationImage() && partIn.part_files.length">
                    <LightBox
                        :ref="`lightbox-${partIn.id}`"
                        :media="media(partIn.part_files)"
                        :showLightBox="false"></LightBox>
                    <div
                        class="images imagescontain"
                        v-if="partIn.part_files.length == 1">
                        <img
                            class="explanation-image objectcontain"
                            v-for="(partFile, i) of partIn.part_files.slice(0, 8)"
                            :key="`explanation-image-${partFile.id}`"
                            :src="partFile.file.public_path"
                            @click="openImageLightBox(partIn.id, i)" />
                    </div>
                    <div
                        class="images"
                        v-else>
                        <img
                            class="explanation-image"
                            v-for="(partFile, i) of partIn.part_files.slice(0, 8)"
                            :key="`explanation-image-${partFile.id}`"
                            :src="partFile.file.public_path"
                            @click="openImageLightBox(partIn.id, i)" />
                    </div>
                    <span
                        class="remaining-images"
                        v-if="partIn.part_files.length > 8"
                        @click="openImageLightBox(partIn.id, 7)">
                        {{ partIn.part_files.length - 8 }}+
                    </span>
                </div>

                <div
                    class="not-found with-header images-not-found explanation-type order-1 order-lg-0"
                    v-else-if="partIn.isExplanationImage() && !partIn.part_files.length">
                    <div
                        class="btn-add-circle icon"
                        v-if="canEditPortfolio"
                        @click="addContent"></div>
                    <div class="alert-notice">{{ $t('assignment.part.explanation.no-images-uploaded') }}</div>
                </div>

                <div
                    class="explanation-video-file explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationVideo()">
                    <design-2021-apprentice-video-player-component
                        class="video-file"
                        v-for="partFile of partIn.part_files"
                        :options="getVideoOptions(partFile.file)"
                        :key="`video-file-${partFile.id}`">
                    </design-2021-apprentice-video-player-component>

                    <div
                        class="not-found with-header empty-video-file-list"
                        v-if="!partIn.part_files.length">
                        <div
                            class="btn-add-circle icon"
                            v-if="canEditPortfolio"
                            @click="addContent"></div>
                        <div class="alert-notice">{{ $t('assignment.part.explanation.no-videos-uploaded') }}</div>
                    </div>
                </div>

                <div
                    class="explanation-attachment-files explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationAttachment()">
                    <div class="component-title">
                        <h5 class="title">{{ $t('common.attachments') }}</h5>
                    </div>

                    <div
                        class="explanation-attachment-file file"
                        v-for="partFile of partIn.part_files"
                        :key="`attachment-file-${partFile.id}`">
                        <div
                            class="icon icon-type"
                            :class="getFileType(partFile.file.extension)"></div>
                        <b-button
                            class="btn-preview-attachment-file"
                            v-if="common.canPreviewFile(partFile.file.extension)"
                            variant="link"
                            @click="previewFile(partFile.file)">
                            {{ partFile.file.full_name }}
                            <div class="file-size text-muted">
                                {{ partFile.file.size }}
                            </div>
                        </b-button>

                        <div
                            class="title"
                            v-else>
                            {{ partFile.file.full_name }}
                            <div class="file-size text-muted">
                                {{ partFile.file.size }}
                            </div>
                        </div>

                        <div class="download-file">
                            <b-button
                                class="btn-download btn-file-download"
                                variant="primary-icon-only"
                                :href="partFile.file.public_path"
                                :download="partFile.file.public_path"
                                target="_blank">
                            </b-button>
                        </div>
                    </div>

                    <div
                        class="not-found with-header empty-file-list"
                        v-if="!partIn.part_files.length">
                        <div
                            class="btn-add-circle icon"
                            v-if="canEditPortfolio"
                            @click="addContent"></div>
                        <div class="alert-notice">{{ $t('assignment.part.explanation.no-videos-uploaded') }}</div>
                    </div>
                </div>

                <div
                    class="explanation-video-link explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationYoutubeLink()">
                    <design-2021-apprentice-video-link-player-component
                        v-if="partIn.video_link.value"
                        :options="playerOptions"
                        :src="sourceUrl">
                    </design-2021-apprentice-video-link-player-component>
                    <div
                        class="not-found with-header video-link-not-found"
                        v-else>
                        <div
                            class="btn-add-circle icon"
                            v-if="canEditPortfolio"
                            @click="addContent"></div>
                        <div class="alert-notice">{{ $t('assignment.part.explanation.no-video-linked') }}</div>
                    </div>
                </div>

                <div
                    class="explanation-audio-files explanation-type order-1 order-lg-0"
                    v-if="partIn.isExplanationAudio()">
                    <div class="component-title">
                        <h5 class="title">{{ $t('assignment.upload_audio') }}</h5>
                    </div>

                    <div
                        class="explanation-audio-file file"
                        v-for="partFile of partIn.part_files"
                        :key="`audio-file-${partFile.id}`">
                        <audio
                            controls
                            :ref="`audio-file-${partFile.id}`">
                            <source
                                :src="partFile.file.public_path"
                                type="audio/mpeg" />
                            {{ $t('assignment.audio_file_not_supported') }}
                        </audio>
                    </div>

                    <div
                        class="not-found with-header audio-file-not-found"
                        v-if="!partIn.part_files.length">
                        <div
                            class="btn-add-circle icon"
                            v-if="canEditPortfolio"
                            @click="addContent"></div>
                        <div class="alert-notice">{{ $t('assignment.no_audio_files_uploaded') }}</div>
                    </div>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import LightBox from 'vue-image-lightbox';
import { ords } from '../../../../../app';

export default {
    name: 'design-2021-assignment-edit-explanation-part-component',
    components: {
        LightBox,
    },
    props: ['partIn', 'pageIn', 'canEditPortfolio'],
    data() {
        return {
            collapsed: true,
            playerOptions: {
                responsive: true,
                autoplay: false,
                controls: true,
            },
            sourceUrl: this.partIn && this.partIn.video_link ? this.partIn.video_link.value : '',
        };
    },
    methods: {
        collapse() {
            this.collapsed = !this.collapsed;
        },

        editPart() {
            this.$bvModal.show(`update-explanation-part-model-${this.partIn.id}`);
        },

        copyPart() {
            this.axios.post(this.routes.assignmentPartCopy(this.partIn.id)).then(
                (response) => {
                    ords.hold();

                    this.pageIn.addRelation('parts', response.data.addedAssignment);

                    response.data.updatedAssignment.forEach((assignment_part) => {
                        ords.use('assignmentPart').update({ order: assignment_part.order }, assignment_part.id);
                    });

                    ords.continue();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        openImageLightBox(partId, i) {
            const lightboxName = 'lightbox-' + partId;
            this.$refs[lightboxName].showImage(i);
        },
        onRemovePart() {
            this.alert
                .confirm(this.$t('assignment.delete_part_title'), this.$t('assignment.confirm_delete_part'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving(this.$t('assignment.delete_part_title'), this.$t('assignment.deleting_part'));

                        this.axios.delete(this.routes.assignmentPartDelete(this.partIn.id)).then(
                            (response) => {
                                this.partIn.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
        getVideoOptions(file) {
            // todo check for unsuported video files
            let ext = file.extension;
            if (ext === 'ogv') {
                ext = 'ogg';
            }
            if (ext === 'mov' || ext === 'MOV') {
                ext = 'mp4';
            }

            return {
                responsive: true,
                autoplay: false,
                controls: true,
                sources: [
                    {
                        type: 'video/' + ext,
                        src: file.public_path,
                    },
                ],
            };
        },
        media(files) {
            return files
                .filter((file) => !!file.file)
                .map((file) => {
                    return {
                        thumb: file.file.path,
                        src: file.file.path,
                        caption: file.name,
                    };
                });
        },

        playAudio(fileId) {
            const name = 'audio-file-' + fileId;
            this.$refs[name][0].play();
            // Todo: feature request add stop button
        },

        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },

        addContent() {
            this.$bvModal.show(`update-explanation-part-model-${this.partIn.id}`);
        },

        previewFile(file) {
            this.$root.$refs.filePreview.show(file);
        },
    },

    watch: {
        partIn(value) {
            this.sourceUrl = value && value.video_link ? value.video_link.value : '';
        },
    },
};
</script>

<style scoped lang="scss">
.explanation-part {
    width: 100%;

    .assignment-explanation-header {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        flex-wrap: wrap;
        border-bottom: 3px dotted $border-color;
        padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');

        .assignment-explanation-header-title {
            display: flex;
            flex-direction: row;
            align-items: center;

            .drag-handle {
                color: $primary;
                margin-right: map-get($grid-gutter-spacing, 'eight');

                &:hover {
                    cursor: pointer;
                }
            }

            .title {
                font-weight: $font-weight-bold;
            }
        }

        .badge-part-type,
        .state-icon {
            align-self: center;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        .badge-part-type {
            margin-left: auto;
            align-self: center;
        }

        .state-icon {
            font-size: $base-font-size;
            transition: transform 400ms ease;
        }

        &.active {
            .state-icon {
                transform: rotate(-180deg);
            }
        }

        &:hover {
            cursor: pointer;
            background-color: $info;
            border-radius: $border-radius-content;

            .title {
                color: $white;
            }
        }
    }

    .explanation-audio-file {
        line-height: 1;

        .btn-audio,
        &.icon {
            display: none;
        }

        .audio-controls ::v-deep {
            height: fit-content;

            &,
            audio,
            .title {
                width: 100%;
            }

            audio {
                max-width: 400px;
            }

            .title {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: flex-start;
                max-width: 100%;
                margin-left: 0;

                .name {
                    width: calc(100% - 40px);
                }

                .btn-remove-audio-file {
                    font-size: 1.3125rem;
                }
            }
        }
    }
}

.explanation-nav ::v-deep {
    .dropdown-toggle {
        line-height: 1;
    }

    .dropdown-menu {
        border: 1px solid $border-color;
    }

    .edit-sub-assignment-part {
        &:before {
            position: relative;
            left: 3px;
            width: 20px;
            margin-right: 12px;
        }
    }

    .copy-sub-assignment-part {
        i {
            color: $primary;
        }
    }

    .remove-sub-assignment-part {
        i {
            color: $danger;
        }
    }

    .remove-sub-assignment-part,
    .copy-sub-assignment-part {
        font-size: $default-font-size;

        .dropdown-item {
            display: flex;
            align-items: center;

            i {
                margin-right: map-get($grid-gutter-spacing, 'eight');
                font-size: $base-font-size;
            }
        }
    }
}

.explanation-content {
    display: flex;
    flex-direction: column;
    flex: 1 0;
    flex-wrap: wrap;
    padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen') 0 26px;

    .explanation-type {
        margin-right: 0;
    }

    .not-found,
    .explanation-type {
        flex: 1;
    }

    .context-explanation-nav {
        margin-left: auto;
        display: flex;
        flex: 1 0 100%;
        justify-content: flex-end;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }
}

.images {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;

    .objectcontain {
        object-fit: contain !important;
        height: auto !important;
    }

    .explanation-image {
        flex: 1 0 100%;
        object-fit: cover;
        object-position: top center;
        border-radius: $border-radius-content;
        height: 350px;
        max-width: 100%;

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        &:hover {
            cursor: pointer;
            filter: brightness(70%);
        }
    }
}

.explanation-images {
    position: relative;

    .remaining-images {
        color: $white;
        font-size: 5rem;
        position: absolute;
        right: 0;
        bottom: calc(175px - 40px);
        left: calc(50% - 40px);
        line-height: 1;
        display: block;
        width: auto;
        text-shadow: 3px 3px $black;
        font-weight: $font-weight-bold;

        &:hover {
            cursor: pointer;
        }
    }
}

.explanation-video-file {
    border-radius: $border-radius-content;
}

.explanation-attachment-files,
.explanation-audio-files {
    .component-title {
        flex: 1 0 100%;
    }

    .file {
        display: flex;
        justify-content: flex-start;
        align-content: center;
        font-size: 1.3125rem;

        .icon {
            align-self: center;
            padding: 10px;
            background: $secondary-background-color;
            border-radius: $border-radius-content;
            color: $primary;
        }

        .title {
            font-size: $default-font-size;
            font-family: $headings-font-family;
            font-weight: $font-weight-bold;
            margin-left: map-get($grid-gutter-spacing, 'ten');
            margin-right: map-get($grid-gutter-spacing, 'ten');
            align-self: center;
            word-break: break-word;

            .file-size {
                font-size: 0.75rem;
                font-weight: $font-weight-normal;
            }
        }

        .download-file {
            display: flex;
            align-items: center;
            align-self: center;
            margin-left: auto;

            .btn-file-download {
                font-size: 1.3125rem;

                &:before {
                    margin-right: 0;
                }
            }
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .explanation-audio-files,
    .explanation-attachment-files {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: wrap;

        .file {
            flex: 1 0 calc(50% - 15px);
            max-width: calc(50% - 15px);

            &:not(:last-child) {
                margin-bottom: 0;
            }

            &:nth-child(odd) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            &:not(:nth-last-child(1)):not(:nth-last-child(2)) {
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }

    .explanation-images {
        .images {
            gap: 30px;
            justify-content: space-between;

            .explanation-image {
                width: calc(50% - 15px);
                flex: 0 0 auto;
                height: 200px;

                &:last-child:is(:nth-child(odd)) {
                    width: 100%;
                }

                &:not(:last-child) {
                    margin-bottom: 0;
                }
            }
        }

        .remaining-images {
            bottom: calc(100px - 40px);
            left: calc(50% + 55px);
        }
    }

    .explanation-audio-files {
        flex-direction: column;

        .file {
            flex: 1 0 100%;
            max-width: 100%;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .explanation-content {
        flex-wrap: nowrap;

        .context-explanation-nav {
            flex: 0 0 auto;
            margin-bottom: 0;
            margin-top: -6px;
        }
    }

    .explanation-images {
        position: relative;

        .imagescontain {
            grid-auto-rows: auto !important;
        }

        .images {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-auto-rows: 170px;
            gap: 15px;

            .explanation-image {
                height: 100%;
                width: 100%;

                &:last-child:is(:nth-child(odd)) {
                    height: 100%;
                }
            }
        }

        .remaining-images {
            bottom: calc(125px - 50px);
            left: calc(100% - 100px);
        }
    }

    //Explanation-images Images grid
    .explanation-image:first-child:nth-last-child(1) {
        grid-row: 1 / 3;
        grid-column: 1 / -1;
    }

    .explanation-image:first-child:nth-last-child(2),
    .explanation-image:first-child:nth-last-child(3),
    .explanation-image:first-child:nth-last-child(4),
    .explanation-image:first-child:nth-last-child(5),
    .explanation-image:first-child:nth-last-child(6),
    .explanation-image:first-child:nth-last-child(7),
    .explanation-image:first-child:nth-last-child(8) {
        grid-row: 1 / 3;
        grid-column: 1 / 3;
    }

    .explanation-image:first-child:nth-last-child(2) ~ .explanation-image {
        grid-row: 3 / -1;
        grid-column: 1 / 3;
    }

    .explanation-image:first-child:nth-last-child(2) ~ .explanation-image:last-child {
        grid-column: 3 / -1;
    }

    .explanation-image:first-child:nth-last-child(3) ~ .explanation-image,
    .explanation-image:first-child:nth-last-child(4) ~ .explanation-image {
        grid-column: 3 / -1;
    }

    .explanation-image:first-child:nth-last-child(4) ~ .explanation-image:last-child {
        grid-column: 1 / -1;
    }

    .explanation-image:first-child:nth-last-child(6) ~ .explanation-image:nth-child(4),
    .explanation-image:first-child:nth-last-child(6) ~ .explanation-image:nth-child(5),
    .explanation-image:first-child:nth-last-child(7) ~ .explanation-image:nth-child(4),
    .explanation-image:first-child:nth-last-child(7) ~ .explanation-image:nth-child(5),
    .explanation-image:first-child:nth-last-child(8) ~ .explanation-image:nth-child(4) {
        grid-row: 2 / 4;
    }

    .explanation-image:first-child:nth-last-child(6) ~ .explanation-image:last-child {
        grid-column: 1 / 3;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .explanation-images {
        .imagescontain {
            grid-auto-rows: auto !important;
        }

        .images {
            grid-auto-rows: 250px;
        }

        .remaining-images {
            bottom: calc(125px - 50px);
            left: calc(100% - 125px - 55px);
        }
    }

    .explanation-audio-files {
        flex-direction: row;

        .file {
            width: calc(50% - 15px);
            flex: 0 0 calc(50% - 15px);
        }

        .btn-audio.icon {
            display: flex;
            align-self: flex-start;
        }

        .audio-controls {
            width: calc(100% - 65px);
            align-self: flex-start;
            margin-left: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}
</style>
